import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Palmtree, Ship, Anchor, Users, Heart, Sparkles, 
  Cake, Star, Calendar, Briefcase, ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';

const services = [
  { 
    icon: Palmtree, 
    title: 'Luxury Resorts', 
    desc: 'Handpicked 5-star properties worldwide',
    details: 'From overwater bungalows in the Maldives to mountain retreats in the Swiss Alps, we curate the finest resort experiences tailored to your preferences.'
  },
  { 
    icon: Ship, 
    title: 'Ocean Cruises', 
    desc: 'Unforgettable voyages at sea',
    details: 'Experience the world from a floating luxury hotel. We partner with top cruise lines to bring you exclusive deals and insider tips.'
  },
  { 
    icon: Anchor, 
    title: 'River Cruises', 
    desc: 'Intimate journeys through scenic waterways',
    details: 'Explore Europe, Asia, or America via picturesque rivers. Perfect for those who want a more intimate, culturally immersive cruise experience.'
  },
  { 
    icon: Users, 
    title: 'Group Travel', 
    desc: 'Coordinated trips for any size group',
    details: 'Whether it\'s a girls trip, guys weekend, or multi-family vacation, we handle all the logistics so everyone can relax and enjoy.'
  },
  { 
    icon: Heart, 
    title: 'Family Vacations', 
    desc: 'Creating lasting memories together',
    details: 'Kid-friendly resorts, multi-generational trips, and family reunion planning. We ensure there\'s something for every age group.'
  },
  { 
    icon: Sparkles, 
    title: 'Honeymoons', 
    desc: 'Romantic escapes for newlyweds',
    details: 'Start your marriage with an unforgettable journey. We specialize in romantic destinations and special touches for newlyweds.'
  },
  { 
    icon: Cake, 
    title: 'Destination Weddings', 
    desc: 'Say "I do" in paradise',
    details: 'From beach ceremonies to castle celebrations, we help plan your dream wedding in stunning locations around the world.'
  },
  { 
    icon: Star, 
    title: 'Anniversary Trips', 
    desc: 'Celebrate your love story',
    details: 'Mark your milestone with a trip you\'ll never forget. We create romantic itineraries perfect for celebrating your journey together.'
  },
  { 
    icon: Calendar, 
    title: 'Birthday Celebrations', 
    desc: 'Milestone trips worth remembering',
    details: 'Whether it\'s a 30th, 50th, or any special birthday, we plan celebrations that match the magnitude of the occasion.'
  },
  { 
    icon: Briefcase, 
    title: 'Corporate Travel', 
    desc: 'Business trips done right',
    details: 'Incentive trips, retreats, and conferences. We handle corporate travel with the same attention to detail as leisure trips.'
  },
];

const ServicesPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-navy py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-gold font-medium tracking-widest text-sm uppercase">What We Offer</span>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white mt-4 mb-6">
            Our Travel Services
          </h1>
          <p className="text-white/80 max-w-2xl mx-auto text-lg">
            From romantic getaways to corporate retreats, we plan every detail of your perfect trip 
            so you can focus on making memories.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div 
                key={index}
                className="bg-cream rounded-2xl p-8 hover:shadow-card-hover transition-all duration-300 group"
              >
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-navy/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-gold/20 transition-colors">
                    <service.icon className="w-8 h-8 text-navy group-hover:text-gold transition-colors" />
                  </div>
                  <div>
                    <h3 className="font-serif text-2xl text-navy mb-2">{service.title}</h3>
                    <p className="text-gold font-medium mb-3">{service.desc}</p>
                    <p className="text-text-secondary leading-relaxed">{service.details}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-navy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">
            Ready to Plan Your Next Adventure?
          </h2>
          <p className="text-white/80 text-lg mb-8">
            Book a consultation and let's create an unforgettable travel experience together.
          </p>
          <Link to="/consultation">
            <Button className="bg-gold hover:bg-gold-dark text-navy font-semibold px-8 py-4 rounded-full text-lg">
              Book Consultation - $25
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
